import { keepPreviousData } from '@tanstack/react-query';
import { trpc } from '../lib/api';
import { App } from 'antd';
import { useState } from 'react';

const getReadableError = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return '不明なエラーが発生しました';
};

export interface FileListFilters {
  fileType?: 'photo' | 'video' | 'document' | 'other';
  storageClass?: string;
  uploadedBy?: string;
  dateFrom?: Date;
  dateTo?: Date;
  searchQuery?: string;
  sortBy?: 'fileName' | 'fileSize' | 'createdAt' | 'lastAccessedAt';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

/**
 * ファイル一覧の取得
 */
export function useFileList(filters: FileListFilters = {}) {
  return trpc.storage.listFiles.useQuery(filters, {
    placeholderData: keepPreviousData, // ページ切り替え時にちらつきを防ぐ
  });
}

/**
 * ファイル操作
 */
export function useFileOperations() {
  const { message } = App.useApp();
  const utils = trpc.useContext();

  const archiveFile = trpc.storage.archiveFile.useMutation({
    onSuccess: () => {
      utils.storage.getCapacity.invalidate();
      utils.storage.listFiles.invalidate();
      message.success('ファイルをアーカイブキューに追加しました');
    },
    onError: (error: unknown) => {
      message.error(`アーカイブに失敗: ${getReadableError(error)}`);
    },
  });

  const bulkDelete = trpc.storage.bulkDelete.useMutation({
    onSuccess: (result) => {
      utils.storage.getCapacity.invalidate();
      utils.storage.listFiles.invalidate();
      message.success(`${result.deleted.count}件のファイルを削除しました`);
    },
    onError: (error: unknown) => {
      message.error(`削除に失敗: ${getReadableError(error)}`);
    },
  });

  const recordAccess = trpc.storage.recordAccess.useMutation();

  return {
    archiveFile,
    bulkDelete,
    recordAccess,
    isArchiving: archiveFile.isPending,
    isDeleting: bulkDelete.isPending,
  };
}

/**
 * ファイル一覧のフィルター管理
 */
export function useFileFilters() {
  const [filters, setFilters] = useState<FileListFilters>({
    page: 1,
    limit: 50,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const updateFilter = <K extends keyof FileListFilters>(key: K, value: FileListFilters[K]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1, // フィルター変更時はページを1にリセット
    }));
  };

  const resetFilters = () => {
    setFilters({
      page: 1,
      limit: 50,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    });
  };

  return {
    filters,
    updateFilter,
    resetFilters,
    setFilters,
  };
}
